import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { translations, Translations } from '../i18n/translations';
import { useUpdateCustomerMutation } from '../api';
import { ru, enUS, fr, nl, de as deLocale } from 'date-fns/locale';
import type { Locale } from 'date-fns';

export type Language = 'ru' | 'en' | 'fr' | 'nl' | 'de';

interface TranslationContextType {
    t: Translations;
    currentLanguage: Language;
    isLoading: boolean;
    changeLanguage: (newLanguage: string, skipApi?: boolean) => Promise<void>;
    locale: string;
    dateFnsLocale: Locale;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

/**
 * Валидирует и нормализует язык
 * @param language - Язык из API или пользовательский ввод
 * @returns Валидный язык (ru, en, fr, nl или de)
 */
const validateLanguage = (language: string | undefined | null): Language => {
    if (!language) return 'en';

    const normalizedLanguage = language.toLowerCase();
    const supported: Language[] = ['ru', 'en', 'fr', 'nl', 'de'];

    if (supported.includes(normalizedLanguage as Language) && translations[normalizedLanguage as Language]) {
        return normalizedLanguage as Language;
    }

    return 'en'; // По умолчанию английский
};

const getLangFromUrlParams = (): Language | null => {
    const params = new URLSearchParams(window.location.search);
    const langParam = params.get('lang');
    if (langParam === '' || langParam === null) return null;

    return validateLanguage(langParam);
};

interface TranslationProviderProps {
    children: ReactNode;
    initialLanguage?: Language;
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({
    children,
    initialLanguage = 'en',
}) => {
    // Загружаем язык из localStorage при инициализации
    const getInitialLanguage = (): Language => {
        const langParam = getLangFromUrlParams();
        if (langParam !== null) {
            return langParam;
        }
        const savedLanguage = localStorage.getItem('app-language') as Language | null;
        if (savedLanguage && translations[savedLanguage]) {
            return savedLanguage;
        }
        return initialLanguage;
    };

    const [currentLanguage, setCurrentLanguage] = useState<Language>(getInitialLanguage());
    const [updateCustomer, { isLoading }] = useUpdateCustomerMutation();

    useEffect(() => {
        document.documentElement.lang = currentLanguage;
    }, [currentLanguage]);

    // Получаем переводы для текущего языка
    const t = translations[currentLanguage];

    // Маппинг языков на локали
    const LOCALE_MAP: Record<Language, string> = {
        ru: 'ru-RU',
        en: 'en-US',
        fr: 'fr-FR',
        nl: 'nl-BE',
        de: 'de-DE',
    };

    // Получаем локаль для текущего языка
    const locale = LOCALE_MAP[currentLanguage] || 'en-US';

    // Маппинг строковых локалей на объекты локалей date-fns
    const dateFnsLocale = useMemo(() => {
        const localeMap: Record<string, Locale> = {
            ru,
            'ru-RU': ru,
            en: enUS,
            'en-US': enUS,
            fr,
            'fr-FR': fr,
            nl,
            'nl-BE': nl,
            de: deLocale,
            'de-DE': deLocale,
        };

        return localeMap[locale] || enUS;
    }, [locale]);

    // Функция для изменения языка
    const changeLanguage = async (newLanguage: string, skipApi: boolean = false) => {
        // Валидация языка
        const validLanguage = validateLanguage(newLanguage);

        // Сохраняем язык в localStorage
        setCurrentLanguage(validLanguage);
        localStorage.setItem('app-language', validLanguage);

        // Если skipApi = true, просто обновляем язык без вызова API
        if (skipApi) {
            return;
        }

        try {
            const newValidLanguage = validLanguage === 'nl' ? 'pl' : validLanguage;
            // Вызываем API метод для обновления языка
            await updateCustomer({ lang: newValidLanguage }).unwrap();
        } catch (error) {
            // В случае ошибки не откатываем язык, так как он уже сохранен в localStorage
            console.error('Failed to update language:', error);
        }
    };

    const value: TranslationContextType = {
        t,
        currentLanguage,
        changeLanguage,
        isLoading,
        locale,
        dateFnsLocale,
    };

    return <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>;
};

// Хук для использования контекста переводов
export const useTranslation = (): TranslationContextType => {
    const context = useContext(TranslationContext);
    if (context === undefined) {
        throw new Error('useTranslation must be used within a TranslationProvider');
    }
    return context;
};
